import { CODE_SYSTEMS, LOINC_LAB_DISPLAY_NAMES } from '@liza/fhir-utils';
import type { ValueSet } from '@medplum/fhirtypes';

const URL = 'https://liza.health/fhir/ValueSet/primary-care-labs';

// Curated lab LOINC codes, sorted numerically by code for readability. Display strings come from
// `LOINC_LAB_DISPLAY_NAMES` (@liza/fhir-utils) — the same curated map the UI renders from — so this
// deployed ValueSet stays consistent with the app. (The pickers read that map directly via
// `useCuratedLabItems` rather than `$expand`, because a terminology server returns the LOINC
// standard display and filters against it.) A test here asserts every code below has a name there.
const LAB_LOINC_CODES = [
  '718-7',
  '751-8',
  '777-3',
  '787-2',
  '1558-6',
  '1742-6',
  '1751-7',
  '1884-6',
  '1920-8',
  '1963-8',
  '1968-7',
  '1975-2',
  '2085-9',
  '2093-3',
  '2132-9',
  '2157-6',
  '2160-0',
  '2191-5',
  '2243-4',
  '2276-4',
  '2339-0',
  '2345-7',
  '2498-4',
  '2500-7',
  '2571-8',
  '2731-8',
  '2777-1',
  '2823-3',
  '2839-9',
  '2842-3',
  '2857-1',
  '2942-1',
  '2951-2',
  '2986-8',
  '2991-8',
  '3016-3',
  '3026-2',
  '3053-6',
  '3084-1',
  '4537-7',
  '4544-3',
  '4548-4',
  '5671-3',
  '5902-2',
  '6299-2',
  '6301-6',
  '6690-2',
  '6768-6',
  '8099-4',
  '9318-7',
  '10501-5',
  '10835-7',
  '11572-5',
  '13457-7',
  '14635-7',
  '14979-9',
  '15067-2',
  '17861-6',
  '19123-9',
  '20150-9',
  '20447-9',
  '21198-7',
  '24467-3',
  '30522-7',
  '32218-0',
  '33452-4',
  '33762-6',
  '33914-3',
  '43583-0',
] as const;

export const lizaPrimaryCareLabs = {
  resourceType: 'ValueSet',
  name: 'liza-primary-care-labs',
  title: 'Liza Primary Care Labs',
  url: URL,
  // Mirror the canonical URL as a FHIR identifier so `deployResources` uses
  // identifier-based idempotency rather than name-based — stable if `name` is
  // ever renamed.
  identifier: [{ system: 'urn:ietf:rfc:3986', value: URL }],
  version: '1',
  status: 'active',
  description: 'Curated set of LOINC codes for lab results commonly entered in Liza primary care workflows.',
  compose: {
    include: [
      {
        system: CODE_SYSTEMS.LOINC,
        concept: LAB_LOINC_CODES.map((code) => ({ code, display: LOINC_LAB_DISPLAY_NAMES[code] })),
      },
    ],
  },
} satisfies ValueSet;

export { LAB_LOINC_CODES };
